import _extends from "@babel/runtime/helpers/extends";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["children", "classes", "className", "overrides", "title", "showIcon", "showCollapse", "type", "defaultCollapsed", "isDismissable", "dismissText", "onDismiss"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import React, { useMemo, useRef, useLayoutEffect, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { Collapse } from 'react-collapse';
import Button from '../Button';
import Icon from '../Icon';
import Text from '../../typography/Text';
import { getOverrides, useClasses } from '../../../utils/overrides';
import { createUseStyles, useTheme } from '../../../utils/styles';
import styles from './styles';
var useStyles = createUseStyles(styles, 'Advice');
var _ref2 = /*#__PURE__*/React.createElement(Icon, {
  name: "arrowDropDown",
  size: "large"
});
function Advice(_ref) {
  var _classnames;
  var children = _ref.children,
    classesProp = _ref.classes,
    classNameProp = _ref.className,
    overridesProp = _ref.overrides,
    title = _ref.title,
    showIcon = _ref.showIcon,
    showCollapse = _ref.showCollapse,
    type = _ref.type,
    defaultCollapsed = _ref.defaultCollapsed,
    isDismissable = _ref.isDismissable,
    dismissText = _ref.dismissText,
    onDismiss = _ref.onDismiss,
    props = _objectWithoutProperties(_ref, _excluded);
  var theme = useTheme();
  var classes = useClasses(useStyles, classesProp);
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    isEllipsisActive = _useState2[0],
    setEllipsisActive = _useState2[1];
  var _useState3 = useState(!defaultCollapsed),
    _useState4 = _slicedToArray(_useState3, 2),
    isOpened = _useState4[0],
    setIsOpened = _useState4[1];
  var textEl = useRef(null);
  useLayoutEffect(function () {
    if (title) return;
    var el = textEl.current;
    setEllipsisActive(el.offsetWidth < el.scrollWidth);

    // Handling resize windows
    var handleResize = function handleResize() {
      return setEllipsisActive(el.offsetWidth < el.scrollWidth);
    };
    window.addEventListener('resize', handleResize);
    return function () {
      window.removeEventListener('resize', handleResize);
    };
  }, [textEl, children, setEllipsisActive, title]);

  // Overrides
  var override = getOverrides(overridesProp, Advice.overrides);

  // Classes
  var rootClassName = classnames(classes.root, (_classnames = {}, _defineProperty(_classnames, classes[type], type), _defineProperty(_classnames, classes.isOpened, isOpened), _classnames), classNameProp);
  var rootProps = _objectSpread(_objectSpread({}, props), {}, {
    className: rootClassName
  });
  var iconProps = useMemo(function () {
    var properties = {
      size: 'medium'
    };
    switch (type) {
      case 'error':
        return _objectSpread(_objectSpread({}, properties), {}, {
          name: 'warningOutline',
          color: theme.colors.red500
        });
      case 'success':
        return _objectSpread(_objectSpread({}, properties), {}, {
          name: 'thickEnabled',
          color: theme.colors.green700
        });
      case 'warning':
        return _objectSpread(_objectSpread({}, properties), {}, {
          name: 'warningRounded',
          color: theme.colors.yellow700
        });
      case 'info':
        return _objectSpread(_objectSpread({}, properties), {}, {
          name: 'infoOutlined',
          color: theme.colors.aqua700
        });
      case 'default':
      default:
        return _objectSpread(_objectSpread({}, properties), {}, {
          name: 'infoOutlined',
          color: theme.colors.neutral700
        });
    }
  }, [theme.colors.aqua700, theme.colors.green700, theme.colors.neutral700, theme.colors.red500, theme.colors.yellow700, type]);
  var toggleCollapsing = useCallback(function () {
    setIsOpened(!isOpened);
  }, [isOpened]);
  var showCollapsingIcon = isEllipsisActive || title;
  return /*#__PURE__*/React.createElement("div", rootProps, /*#__PURE__*/React.createElement("div", {
    className: classes.wrap
  }, showIcon && /*#__PURE__*/React.createElement("div", _extends({
    className: classes.icon
  }, override.icon), /*#__PURE__*/React.createElement(Icon, iconProps)), !showCollapse && /*#__PURE__*/React.createElement(Text, _extends({
    className: classes.Text
  }, override.Text, {
    overrides: {
      root: {
        ref: textEl
      }
    }
  }), children), showCollapse && /*#__PURE__*/React.createElement(Collapse, _extends({
    isOpened: isOpened || false
  }, override['react-collapse']), /*#__PURE__*/React.createElement("div", _extends({
    className: classes.collapseContainer
  }, override.collapseContainer), /*#__PURE__*/React.createElement("div", _extends({
    className: classes.textContainer
  }, override.textContainer), /*#__PURE__*/React.createElement(Text, _extends({
    isTruncated: !isOpened,
    className: classes.Text,
    bold: !!title
  }, override.Text, {
    overrides: {
      root: {
        ref: textEl
      }
    }
  }), !title && children, title && title), title && /*#__PURE__*/React.createElement("div", {
    className: classes.withTitleContainer
  }, /*#__PURE__*/React.createElement(Text, _extends({
    className: classes.Text
  }, override.Text, {
    overrides: {
      root: {
        ref: textEl
      }
    }
  }), children))), showCollapsingIcon && /*#__PURE__*/React.createElement("span", _extends({
    onClick: toggleCollapsing,
    className: classes.dropdownIcon
  }, override.dropdownIcon), _ref2)))), isDismissable && /*#__PURE__*/React.createElement(Button, {
    className: classes.dismiss,
    onClick: onDismiss,
    type: "terciary",
    size: "small"
  }, dismissText));
}
Advice.overrides = ['root', 'icon', 'textContainer', 'collapseContainer', 'Text', 'dropdownIcon', 'react-collapse'];
Advice.defaultProps = {
  className: '',
  overrides: {},
  showIcon: false,
  showCollapse: true,
  type: 'default',
  defaultCollapsed: true,
  isDismissable: false
};
process.env.NODE_ENV !== "production" ? Advice.propTypes = {
  children: PropTypes.node.isRequired,
  title: PropTypes.any,
  className: PropTypes.string,
  overrides: PropTypes.object,
  showIcon: PropTypes.bool,
  showCollapse: PropTypes.bool,
  defaultCollapsed: PropTypes.bool,
  type: PropTypes.oneOf(['default', 'error', 'info', 'success', 'warning']),
  isDismissable: PropTypes.bool,
  dismissText: PropTypes.string,
  onDismiss: PropTypes.func
} : void 0;
export default /*#__PURE__*/React.memo(Advice);